import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { attT } from "@/lib/attendanceI18n";

/**
 * STAS → Per-user UI Feature Overrides.
 */
export default function UserFeaturesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, dir, lang } = useI18n();

  const [catalog, setCatalog] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const isRoot = !!user?.role?.is_root;

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [cat, hid, usr] = await Promise.all([
        api.get("/admin/users/features/catalog"),
        api.get(`/admin/users/${userId}/features`),
        api.get(`/users/${userId}`).catch(() => ({ data: null })),
      ]);
      setCatalog(cat.data.features || []);
      setHidden(new Set(hid.data.hidden_features || []));
      setTarget(usr.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const isEn = lang === "en";
    const map = new Map();
    for (const f of catalog) {
      const g = isEn ? (f.group_en || f.group) : f.group;
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(f);
    }
    return Array.from(map.entries());
  }, [catalog, lang]);

  const toggle = (key) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key); else next.add(key);
    setHidden(next);
  };

  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.put(`/admin/users/${userId}/features`, { hidden_features: Array.from(hidden) });
      setOk(attT(lang, "uf.save_ok"));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reset = () => setHidden(new Set());

  if (!isRoot) {
    return <div style={{ padding: 40, color: theme.errorText }}>{attT(lang, "uf.stas_only")}</div>;
  }

  const s = makeStyles(theme);

  return (
    <div dir={dir} style={s.page} data-testid="user-features-page">
      <div style={s.container}>
        <div style={s.header}>
          <button onClick={() => navigate("/admin/users")} style={s.backBtn} data-testid="user-features-back">
            {attT(lang, dir === "rtl" ? "back" : "back_ltr")}
          </button>
          <div>
            <h1 style={s.title}>{attT(lang, "uf.title")}</h1>
            <div style={s.sub}>{attT(lang, "uf.user_label")}: {target?.full_name || target?.username || userId}</div>
          </div>
        </div>

        {err && <div style={s.err} data-testid="user-features-error">{err}</div>}
        {ok && <div style={s.ok} data-testid="user-features-ok">{ok}</div>}

        {loading ? (
          <div style={s.loading}>{attT(lang, "loading")}</div>
        ) : (
          <>
            <div style={s.info} data-testid="user-features-info">
              {attT(lang, "uf.info")}{" "}
              <span>
                <strong data-testid="user-features-hidden-count">{hidden.size}</strong>{" "}
                {attT(lang, "uf.hidden_count", { n: "" }).replace(/\{n\}\s*/, "")}
              </span>
            </div>

            {grouped.map(([group, items]) => (
              <section key={group} style={s.section}>
                <h2 style={s.sectionTitle}>{group}</h2>
                <div style={s.grid}>
                  {items.map((f) => {
                    const isHidden = hidden.has(f.key);
                    const label = lang === "en" ? (f.label_en || f.label_ar) : f.label_ar;
                    return (
                      <label
                        key={f.key}
                        style={{
                          ...s.row,
                          background: isHidden ? "rgba(212,175,55,0.10)" : theme.surface,
                          borderColor: isHidden ? theme.gold : theme.border,
                        }}
                        data-testid={`feature-row-${f.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={isHidden}
                          onChange={() => toggle(f.key)}
                          data-testid={`feature-toggle-${f.key}`}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={s.rowLabel}>{label}</div>
                          <div style={s.rowKey}>{f.key}</div>
                        </div>
                        <span style={{ ...s.tag, background: isHidden ? theme.gold : "transparent", color: isHidden ? "#000" : theme.textMuted }}>
                          {isHidden ? attT(lang, "uf.tag.hidden") : attT(lang, "uf.tag.visible")}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </section>
            ))}

            <div style={s.actions}>
              <button onClick={save} disabled={busy} style={s.btnPrimary} data-testid="user-features-save">
                {busy ? attT(lang, "saving") : attT(lang, "uf.save")}
              </button>
              <button onClick={reset} disabled={busy} style={s.btn} data-testid="user-features-reset">
                {attT(lang, "uf.reset")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const makeStyles = (theme) => ({
  page: { minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: "'Tajawal','Segoe UI', sans-serif" },
  container: { maxWidth: 980, margin: "0 auto", padding: "24px 16px" },
  header: { display: "flex", gap: 16, alignItems: "center", marginBottom: 24 },
  backBtn: { background: "transparent", color: theme.text, border: `1px solid ${theme.border}`, padding: "8px 14px", borderRadius: 8, cursor: "pointer" },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: theme.gold },
  sub: { color: theme.textMuted, fontSize: 13 },
  info: { background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`, borderRadius: 12, padding: 14, marginBottom: 18, fontSize: 13, lineHeight: 1.8 },
  err: { background: theme.errorBg, color: theme.errorText, padding: 10, borderRadius: 8, marginBottom: 12 },
  ok: { background: "rgba(48,164,108,0.15)", color: "#30a46c", padding: 10, borderRadius: 8, marginBottom: 12 },
  loading: { padding: 40, textAlign: "center", color: theme.textMuted },
  section: { marginBottom: 18 },
  sectionTitle: { fontSize: 14, fontWeight: 800, color: theme.gold, margin: "0 0 10px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 8 },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, border: `1px solid ${theme.border}`, cursor: "pointer", transition: "background 120ms, border-color 120ms" },
  rowLabel: { fontSize: 14, fontWeight: 700 },
  rowKey: { fontSize: 11, color: theme.textMuted, marginTop: 2 },
  tag: { fontSize: 11, padding: "3px 10px", borderRadius: 999, fontWeight: 800, border: `1px solid ${theme.border}` },
  actions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20, flexWrap: "wrap" },
  btn: { background: theme.surfaceCard, color: theme.text, border: `1px solid ${theme.border}`, padding: "10px 18px", borderRadius: 10, cursor: "pointer", fontWeight: 700 },
  btnPrimary: { background: theme.gold, color: "#000", border: "none", padding: "10px 22px", borderRadius: 10, cursor: "pointer", fontWeight: 800 },
});
